import { useQuery } from '@apollo/client'
import { useEffect, useMemo, useRef, useState } from 'react'

import { GET_PROJECT_DOCUMENTS_PREVIEWS } from 'data/layoutcreator/queries/documents'
import { fetchImageBlob } from 'shared/helpers/file'
import { Base64, Url } from 'shared/types/global'
import { ProjectDocument } from 'shared/types/layoutcreator/graphql'

import { usePreviewsCache } from './usePreviewsCache'

export type PagePreview = {
  url: string | null
  isUrlLoading: boolean
  source?: PreviewsSource
}

/*
  There are 3 sources of previews (sorted by priority):
  1. Cache
  2. LC
  3. Chili

  If there is no preview in cache, we check if there is a preview
  at Layout Creator server or take the one Chili provides
  and cache it for the next load.

  At the moment, we save previews on LC server only when saving pages after editing
  since preview generation after editing is very slow on Chili's side.
*/

export enum PreviewsSource {
  cache = 'cache',
  lc = 'lc',
  chili = 'chili'
}

export const usePagePreviews = ({
  pages,
  projectId,
  groupId
}: {
  pages: ProjectDocument[]
  projectId: string
  groupId: string
}) => {
  const [cachedPreviews, setCachedPreviews] = useState<Record<
    string,
    Base64
  > | null>(null)
  const { cachePreview, getPreviewFromCache } = usePreviewsCache()

  const { data: customPreviewsData, loading: customPreviewsLoading } =
    useQuery(GET_PROJECT_DOCUMENTS_PREVIEWS, {
      variables: {
        projectId: projectId,
        groupId: groupId
      }
    })

  const chiliPreviewsMap: Record<string, Url> = useMemo(() => {
    const entries = pages.map(page => {
      return [page.id, page.previewUrl]
    })

    return Object.fromEntries(entries || [])
  }, [pages])

  const customPreviewsMap = useMemo(() => {
    if (customPreviewsLoading) {
      return null
    }

    const entries =
      customPreviewsData?.getProjectDocumentsPreviews.map(page => {
        if (!page.path) {
          return []
        }

        return [
          page.id,
          `${process.env.REACT_APP_REST_URL}${page.path}`
        ]
      })

    return Object.fromEntries(entries || [])
  }, [customPreviewsData])

  const isItemsInitialized = useRef<boolean>(false)

  useEffect(() => {
    if (!pages.length || isItemsInitialized.current) {
      return
    }

    isItemsInitialized.current = true

    // Load previews that are cached into state
    const fetchPreviews = async () => {
      const previewsMap: Record<string, string> = {}

      for (const item of pages) {
        const url = await getPreviewFromCache(
          item.id,
          new Date(item.updatedAt) || null
        )

        if (!url) {
          continue
        }

        previewsMap[item.id] = url
      }

      setCachedPreviews(previewsMap)
    }

    fetchPreviews()
  }, [pages])

  const loadAndCachePreview = async (
    id: string,
    url: string
  ): Promise<void> => {
    try {
      const blob = await fetchImageBlob(url)
      await cachePreview(id, blob)
    } catch (error) {
      console.error('Error loading or caching preview', error)
    }
  }

  const getPagePreview = (id: string): PagePreview => {
    if (cachedPreviews === null) {
      return {
        url: null,
        isUrlLoading: true
      }
    }

    if (cachedPreviews?.[id]) {
      return {
        url: cachedPreviews[id],
        isUrlLoading: false,
        source: PreviewsSource.cache
      }
    }

    if (!customPreviewsMap && customPreviewsLoading) {
      return {
        url: null,
        isUrlLoading: true
      }
    }

    const lcUrl = customPreviewsMap[id] || null
    const chiliUrl = chiliPreviewsMap[id] || null

    // If there is no preview in cache, load and cache it
    if (lcUrl || chiliUrl) {
      loadAndCachePreview(id, (lcUrl || chiliUrl) as string)
    }

    return {
      url: (lcUrl || chiliUrl) as string,
      isUrlLoading: false,
      source: lcUrl ? PreviewsSource.lc : PreviewsSource.chili
    }
  }

  return { getPagePreview }
}
